import { FC, useRef } from "react";

import Box from "@mui/material/Box";
import { SxProps, Theme } from "@mui/material/styles";
import { browserDefaultTimezone } from "@simplyk/common";
import { useFormContext, useWatch } from "react-hook-form";

import { useRateScheduleDigest } from "../../../RatesDigest/RatesDigestContext";
import { useRatesExpansion } from "../../../RatesExpansion/RatesExpansionContext";
import { useRateContext } from "../../Context/RateContext";
import { useRateFieldName } from "../../hooks/useRateFieldName";

import { resolveScheduleDates } from "../../../helpers/resolveScheduleDates";

import { buildExtrasSummary, ExtrasSummaryIcon, ExtrasSummaryPart } from "./buildExtrasSummary";
import { summaryToneVibes } from "./summaryTone";
import { useSummaryOverflow } from "@/common/hooks/useSummaryOverflow";

import { useCurrentUserContext } from "@/common/contexts/CurrentUserContext";
import { useLocaleContext } from "@/common/contexts/LocaleContext";
import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardIcon } from "@/dashboard-design-system/Icon/DashboardIcon";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";
import { useStableLiveFormEditorContext } from "@/features/LiveFormEditor/common/LiveFormEditorContext";
import { StepTicketsFormData } from "@/features/LiveFormEditor/Steps/StepTickets/types";
import { Card, Date as DateIcon } from "@/icons/outlined";
import { IconElement } from "@/icons/types";

const ITEM_GAP_PX = 6;

const partIcons: Record<ExtrasSummaryIcon, IconElement> = {
  schedule: <DateIcon />,
  memberOnly: <Card />,
};

const summaryRowSx = (hidden: boolean): SxProps<Theme> => ({
  display: "flex",
  alignItems: "center",
  gap: `${ITEM_GAP_PX}px`,
  whiteSpace: "nowrap",
  ...(hidden && {
    position: "absolute",
    top: 0,
    left: 0,
    visibility: "hidden",
    pointerEvents: "none",
    width: "max-content",
  }),
});

interface ExtrasSummaryProps {
  now?: Date;
}

export const ExtrasSummary: FC<ExtrasSummaryProps> = ({ now }) => {
  const { t } = useTranslate();
  const { locale } = useLocaleContext();
  const { organizationCurrency } = useCurrentUserContext();
  const { apiFormObject: apiTicketing } = useStableLiveFormEditorContext<"ticketing">();
  const { rateId, isRegistration, formCategory } = useRateContext();
  const { setExpandedRateId } = useRatesExpansion();
  const { control } = useFormContext<StepTicketsFormData>();

  const containerRef = useRef<HTMLElement | null>(null);
  const measureRef = useRef<HTMLElement | null>(null);

  const isGroup = useWatch({ control, name: useRateFieldName("isGroup") });
  const attendeesByTicket = useWatch({ control, name: useRateFieldName("attendeesByTicket") });
  const isScheduled = useWatch({ control, name: useRateFieldName("isScheduled") });
  const salesStartMode = useWatch({ control, name: useRateFieldName("salesStartMode") });
  const salesStartDate = useWatch({ control, name: useRateFieldName("salesStartDate") });
  const salesStartAfterRateId = useWatch({ control, name: useRateFieldName("salesStartAfterRateId") });
  const salesEndMode = useWatch({ control, name: useRateFieldName("salesEndMode") });
  const salesEndDate = useWatch({ control, name: useRateFieldName("salesEndDate") });
  const allRates = useRateScheduleDigest();
  const isMemberOnly = useWatch({ control, name: useRateFieldName("isMemberOnly") });
  const isGeneratingReceipt = useWatch({ control, name: useRateFieldName("isGeneratingReceipt") });
  const receiptEligibleAmount = useWatch({ control, name: useRateFieldName("receiptEligibleAmount") });
  const isPayWhatYouCan = useWatch({ control, name: useRateFieldName("isPayWhatYouCan") });
  const categoryIds = useWatch({ control, name: useRateFieldName("categoryIds") });

  const resolvedSchedule = resolveScheduleDates(
    { isScheduled, salesStartMode, salesStartDate, salesStartAfterRateId, salesEndMode, salesEndDate },
    allRates
  );

  const parts = buildExtrasSummary(
    {
      isGroup,
      attendeesByTicket,
      isScheduled,
      scheduleStartDate: resolvedSchedule.startDate,
      scheduleEndDate: resolvedSchedule.endDate,
      isMemberOnly,
      isGeneratingReceipt,
      receiptEligibleAmount,
      isPayWhatYouCan,
      categoryIds,
    },
    {
      t,
      locale,
      currency: organizationCurrency,
      timezone: apiTicketing.eventTimezone || browserDefaultTimezone,
      now: now ?? new Date(),
      isRegistration,
      formCategory,
    }
  );

  const itemsKey = parts.map((part) => part.text).join("|");
  const visibleCount = useSummaryOverflow({
    containerRef,
    measureRef,
    itemCount: parts.length,
    itemsKey,
    gapPx: ITEM_GAP_PX,
  });

  if (!parts.length) {
    return null;
  }

  const hiddenCount = parts.length - visibleCount;

  return (
    <Box
      ref={containerRef}
      onClick={() => setExpandedRateId(rateId)}
      data-test="rate-extras-summary"
      sx={{ cursor: "pointer", minWidth: 0, position: "relative", overflow: "hidden" }}
    >
      <Box sx={summaryRowSx(false)}>
        {parts.slice(0, visibleCount).map((part, index) => (
          <SummaryItem key={part.key} part={part} withBullet={index > 0} />
        ))}
        {hiddenCount > 0 && (
          <SummaryCounter
            withBullet={visibleCount > 0}
            label={t("dashboard", "ticketing.extrasSummary.moreOptions", { count: hiddenCount })}
          />
        )}
      </Box>
      <Box ref={measureRef} aria-hidden sx={summaryRowSx(true)}>
        {parts.map((part, index) => (
          <SummaryItem key={part.key} part={part} withBullet={index > 0} />
        ))}
        <SummaryCounter
          withBullet
          label={t("dashboard", "ticketing.extrasSummary.moreOptions", { count: parts.length })}
        />
      </Box>
    </Box>
  );
};

const SummaryItem: FC<{ part: ExtrasSummaryPart; withBullet: boolean }> = ({ part, withBullet }) => (
  <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
    {withBullet ? <Bullet /> : null}
    {part.icon ? (
      <DashboardIcon size="small" vibe={summaryToneVibes[part.tone]}>
        {partIcons[part.icon]}
      </DashboardIcon>
    ) : null}
    <DashboardTypography component="span" variant="caption" vibe={summaryToneVibes[part.tone]}>
      {part.text}
    </DashboardTypography>
  </Box>
);

const SummaryCounter: FC<{ withBullet: boolean; label: string }> = ({ withBullet, label }) => (
  <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
    {withBullet ? <Bullet /> : null}
    <DashboardTypography component="span" variant="caption" vibe="text-neutral-quiet">
      {label}
    </DashboardTypography>
  </Box>
);

const Bullet: FC = () => (
  <Box component="span" sx={(theme) => ({ color: theme.palette.text.neutral.quiet })}>
    •
  </Box>
);
